/**
 * AI Console Dashboard Page
 *
 * Operator-first overview of usage, cost, risk, and investigation hotspots.
 */

import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/layout/Layout'
import {
  Page,
  PageBody,
  SectionHeader,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelDescription,
  PanelBody,
  Button,
  Badge,
  Skeleton,
} from '../../components/ui'
import { MetricCard, MetricCardGroup } from '../../components/ui/metric-card'
import { LineChart, BarChart, ChartContainer } from '../../components/ui/chart'
import { AIConsoleShell, type AIConsoleShellSummaryItem } from '../../components/ai-console/AIConsoleShell'
import {
  useDashboardMetrics,
  useAppletSummaries,
  useAppletTraceMetrics,
  useDailyMetrics,
} from '../../hooks/useAIConsole'
import type { DashboardFilters, AppletTraceMetrics } from './types'
import {
  PERIOD_OPTIONS,
  appletLabel,
  fmtCost,
  fmtDuration as fmtLatency,
  fmtPct,
  fmtDate,
} from './constants'

type AlertLevel = 'danger' | 'warning' | 'info'

interface AlertItem {
  level: AlertLevel
  title: string
  detail: string
  applet?: string
}

const ALERT_RANK: Record<AlertLevel, number> = {
  danger: 3,
  warning: 2,
  info: 1,
}

const AIConsoleDashboardPage: React.FC = () => {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<DashboardFilters>({ period_days: 7 })

  const { data: metrics, isLoading: metricsLoading } = useDashboardMetrics(filters)
  const { data: applets, isLoading: appletsLoading } = useAppletSummaries()
  const { data: appletMetrics, isLoading: appletMetricsLoading } = useAppletTraceMetrics(filters.period_days)
  const { data: dailyMetrics, isLoading: dailyLoading } = useDailyMetrics(filters.period_days, filters.applet_id)

  const enrichedApplets = useMemo(() => {
    if (!applets) return []
    const metricsByApplet = new Map<string, AppletTraceMetrics>()
    appletMetrics?.forEach((metric) => metricsByApplet.set(metric.applet_id, metric))

    return applets
      .map((applet) => ({
        ...applet,
        trace_count: metricsByApplet.get(applet.applet_id)?.trace_count ?? 0,
        total_cost_usd: metricsByApplet.get(applet.applet_id)?.total_cost_usd ?? 0,
        avg_latency_ms: metricsByApplet.get(applet.applet_id)?.avg_latency_ms ?? 0,
        error_count: metricsByApplet.get(applet.applet_id)?.error_count ?? 0,
        unique_users: metricsByApplet.get(applet.applet_id)?.unique_users ?? 0,
      }))
      .sort((left, right) => right.trace_count - left.trace_count)
  }, [applets, appletMetrics])

  const alerts = useMemo<AlertItem[]>(() => {
    const items: AlertItem[] = []

    if (metrics && metrics.error_count > 0) {
      const rate = metrics.total_traces > 0 ? (metrics.error_count / metrics.total_traces) * 100 : 0
      items.push({
        level: rate >= 10 ? 'danger' : 'warning',
        title: 'Global failure rate is elevated',
        detail: `${metrics.error_count} errors over ${metrics.total_traces} runs in the last ${filters.period_days} days (${rate.toFixed(1)}%).`,
      })
    }

    enrichedApplets.forEach((applet) => {
      if (applet.avg_latency_ms >= 5000) {
        items.push({
          level: 'warning',
          title: `${appletLabel(applet.applet_id)} is slowing down`,
          detail: `Average latency is ${fmtLatency(applet.avg_latency_ms)}. Inspect trace hotspots before iterating prompts.`,
          applet: applet.applet_id,
        })
      }
      if (applet.trace_count > 0 && applet.error_count / applet.trace_count >= 0.2) {
        items.push({
          level: 'danger',
          title: `${appletLabel(applet.applet_id)} is unstable`,
          detail: `${fmtPct(applet.error_count, applet.trace_count)} of recent runs ended in error.`,
          applet: applet.applet_id,
        })
      }
      if (applet.total_cost_usd > 0 && metrics && metrics.total_cost_usd > 0 && applet.total_cost_usd / metrics.total_cost_usd >= 0.4) {
        items.push({
          level: 'info',
          title: `${appletLabel(applet.applet_id)} dominates spend`,
          detail: `${fmtCost(applet.total_cost_usd)} accounts for ${fmtPct(applet.total_cost_usd, metrics.total_cost_usd)} of total spend.`,
          applet: applet.applet_id,
        })
      }
    })

    return items.sort((left, right) => ALERT_RANK[right.level] - ALERT_RANK[left.level])
  }, [enrichedApplets, filters.period_days, metrics])

  const topAlert = alerts[0]

  const tracesChartData = useMemo(() => {
    if (!dailyMetrics?.length) return null
    return {
      labels: dailyMetrics.map((metric) => fmtDate(metric.date)),
      datasets: [
        {
          label: 'Runs',
          data: dailyMetrics.map((metric) => metric.trace_count),
        },
      ],
    }
  }, [dailyMetrics])

  const costChartData = useMemo(() => {
    if (!dailyMetrics?.length) return null
    return {
      labels: dailyMetrics.map((metric) => fmtDate(metric.date)),
      datasets: [
        {
          label: 'Cost ($)',
          data: dailyMetrics.map((metric) => metric.total_cost_usd),
        },
      ],
    }
  }, [dailyMetrics])

  const hotspotChartData = useMemo(() => {
    if (!enrichedApplets.length) return null
    const topHotspots = enrichedApplets.slice(0, 6)
    return {
      labels: topHotspots.map((applet) => appletLabel(applet.applet_id)),
      datasets: [
        {
          label: 'Spend ($)',
          data: topHotspots.map((applet) => applet.total_cost_usd),
        },
      ],
    }
  }, [enrichedApplets])

  const errorRate = metrics && metrics.total_traces > 0 ? fmtPct(metrics.error_count, metrics.total_traces) : '0%'
  const stableApplets = enrichedApplets.filter((applet) => applet.trace_count > 0 && applet.error_count === 0).length
  const selectedAppletMetrics = filters.applet_id
    ? enrichedApplets.find((applet) => applet.applet_id === filters.applet_id)
    : undefined
  const selectedAppletLabel = filters.applet_id ? appletLabel(filters.applet_id) : 'All Applets'
  const highlightedApplet = filters.applet_id ?? topAlert?.applet
  const shellSummary: AIConsoleShellSummaryItem[] = [
    {
      label: 'Window',
      value: `${filters.period_days} days`,
      detail: 'Keep your investigation inside one recent operating window so trends and regressions stay comparable.',
    },
    {
      label: 'Current focus',
      value: selectedAppletLabel,
      detail: filters.applet_id
        ? 'You are already narrowed to one product area, which is the right mode for investigation.'
        : 'You are looking across every mapped applet to decide where to dive deeper.',
      tone: filters.applet_id ? ('brand' as const) : ('default' as const),
    },
    {
      label: 'Runs in view',
      value: selectedAppletMetrics?.trace_count ?? metrics?.total_traces ?? 0,
      detail: filters.applet_id ? 'Mapped runs for this applet in the active window.' : 'Mapped runs across all applets in the active window.',
    },
    {
      label: topAlert ? 'Strongest signal' : 'Healthy applets',
      value: topAlert ? topAlert.title : `${stableApplets} stable`,
      detail: topAlert
        ? topAlert.detail
        : 'Use the queue below to verify where you can safely move from observation into prompt iteration.',
      tone: topAlert?.level === 'danger' ? 'danger' : topAlert?.level === 'warning' ? 'warning' : 'success',
    },
  ]

  return (
    <Layout>
      <Page>
        <PageBody>
          <AIConsoleShell
            sectionId="dashboard"
            title="Start with the operating picture, then move into a specific run."
            description="This is the front door for the AI Console. It shows what changed, where risk or spend is rising, and which applet deserves the next investigation."
            plainLanguageSummary="Use this page to answer one question first: where should I look next? Once you see a signal here, switch to Inspect Runs or Try Changes without losing the applet you were focused on."
            actions={
              <div className="flex flex-wrap items-center gap-2">
                {PERIOD_OPTIONS.map((option) => (
                  <Button
                    key={option.value}
                    variant={filters.period_days === option.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilters((current) => ({ ...current, period_days: option.value }))}
                  >
                    {option.label}
                  </Button>
                ))}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    navigate(
                      highlightedApplet
                        ? `/ai-console/agent-lens?applet=${highlightedApplet}`
                        : '/ai-console/agent-lens',
                    )
                  }
                >
                  Inspect Runs
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(
                      highlightedApplet
                        ? `/ai-console/agent-testing?applet=${highlightedApplet}`
                        : '/ai-console/agent-testing',
                    )
                  }
                >
                  Try Changes
                </Button>
              </div>
            }
            summaryItems={shellSummary}
          />

          <Panel className="mb-6 mt-6">
            <PanelHeader>
              <PanelTitle>Filter the map before you investigate</PanelTitle>
              <PanelDescription>
                Narrow to one applet when you are ready to explain a problem, test a prompt change, or share a finding with a non-technical teammate.
              </PanelDescription>
            </PanelHeader>
            <PanelBody className="flex items-center gap-3 flex-wrap">
            <Button
              variant={!filters.applet_id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilters((current) => ({ ...current, applet_id: undefined }))}
            >
              All Applets
            </Button>
            {applets?.map((applet) => (
              <Button
                key={applet.applet_id}
                variant={filters.applet_id === applet.applet_id ? 'default' : 'outline'}
                size="sm"
                onClick={() =>
                  setFilters((current) => ({
                    ...current,
                    applet_id: current.applet_id === applet.applet_id ? undefined : applet.applet_id,
                  }))
                }
              >
                {appletLabel(applet.applet_id)}
                <span className="text-[10px] opacity-70">
                  {enrichedApplets.find((item) => item.applet_id === applet.applet_id)?.trace_count ?? 0}
                </span>
              </Button>
            ))}
            </PanelBody>
          </Panel>

          <div className="grid grid-cols-1 xl:grid-cols-[1.15fr,0.85fr] gap-6">
            <Panel className="rounded-[28px] border-gray-200/80 shadow-[0_1px_0_rgba(255,255,255,0.8)_inset,0_24px_48px_-36px_rgba(43,20,32,0.28)] dark:border-dark-border/60 dark:shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_26px_56px_-40px_rgba(0,0,0,0.52)]">
              <PanelHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <PanelTitle>What Needs Attention First</PanelTitle>
                    <PanelDescription>
                      Start with the sharpest signal here, then move into Inspect Runs to explain why it happened.
                    </PanelDescription>
                  </div>
                  <Badge variant={topAlert ? (topAlert.level === 'danger' ? 'danger' : topAlert.level === 'warning' ? 'warning' : 'outline') : 'success'}>
                    {topAlert ? topAlert.level : 'healthy'}
                  </Badge>
                </div>
              </PanelHeader>
              <PanelBody>
                {metricsLoading ? (
                  <Skeleton variant="rounded" height={180} />
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-gray-200 bg-gray-50/80 p-5 dark:border-dark-border/50 dark:bg-dark-surface-2/60">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
                        Primary Signal
                      </p>
                      <h2 className="mt-2 text-xl font-semibold text-charcoal dark:text-gray-100">
                        {topAlert ? topAlert.title : 'No major anomalies in the selected window'}
                      </h2>
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                        {topAlert
                          ? topAlert.detail
                          : `You have ${metrics?.total_traces ?? 0} runs across ${filters.period_days} days with ${stableApplets} applets showing zero recent errors.`}
                      </p>
                      <div className="mt-4 flex items-center gap-2">
                        <Button
                          onClick={() =>
                            navigate(
                              topAlert?.applet
                                ? `/ai-console/agent-lens?applet=${topAlert.applet}`
                                : '/ai-console/agent-lens',
                            )
                          }
                        >
                          Investigate in Agent Lens
                        </Button>
                        <Button variant="outline" onClick={() => navigate('/ai-console/agent-testing')}>
                          Open testing hub
                        </Button>
                      </div>
                    </div>

                    <MetricCardGroup columns={4}>
                      <MetricCard label="Runs" value={metrics?.total_traces ?? 0} loading={metricsLoading} />
                      <MetricCard label="Active Users" value={metrics?.unique_users ?? 0} loading={metricsLoading} />
                      <MetricCard label="Spend" value={fmtCost(metrics?.total_cost_usd ?? 0)} loading={metricsLoading} />
                      <MetricCard
                        label="Failure Rate"
                        value={errorRate}
                        loading={metricsLoading}
                        trend={
                          metrics && metrics.error_count > 0
                            ? { direction: 'up', value: `${metrics.error_count} errors`, favorable: false }
                            : { direction: 'flat', value: 'No recent errors', favorable: true }
                        }
                      />
                    </MetricCardGroup>
                  </div>
                )}
              </PanelBody>
            </Panel>

            <Panel className="rounded-[28px] border-gray-200/80 shadow-[0_1px_0_rgba(255,255,255,0.8)_inset,0_24px_48px_-36px_rgba(43,20,32,0.28)] dark:border-dark-border/60 dark:shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_26px_56px_-40px_rgba(0,0,0,0.52)]">
              <PanelHeader>
                <PanelTitle>Hotspot Board</PanelTitle>
                <PanelDescription>
                  The applets absorbing the most operational attention.
                </PanelDescription>
              </PanelHeader>
              <PanelBody>
                {appletMetricsLoading ? (
                  <div className="space-y-3">
                    {[...Array(4)].map((_, index) => (
                      <Skeleton key={index} variant="rounded" height={72} />
                    ))}
                  </div>
                ) : enrichedApplets.length > 0 ? (
                  <div className="space-y-3">
                    {enrichedApplets.slice(0, 5).map((applet) => (
                      <div
                        key={applet.applet_id}
                        className="rounded-[22px] border border-gray-200/85 bg-white px-4 py-4 text-left shadow-[0_1px_0_rgba(255,255,255,0.82)_inset,0_16px_32px_-30px_rgba(43,20,32,0.22)] transition-[transform,box-shadow,border-color] duration-150 ease-[cubic-bezier(0.2,0,0,1)] hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-[0_1px_0_rgba(255,255,255,0.92)_inset,0_22px_42px_-30px_rgba(43,20,32,0.3)] dark:border-dark-border/50 dark:bg-dark-surface dark:shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_18px_40px_-32px_rgba(0,0,0,0.45)]"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-charcoal dark:text-gray-100">
                              {appletLabel(applet.applet_id)}
                            </p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {applet.trace_count} runs · {applet.unique_users} active users
                            </p>
                          </div>
                          <Badge variant={applet.error_count > 0 ? 'danger' : 'outline'}>
                            {applet.error_count > 0 ? `${applet.error_count} errors` : 'stable'}
                          </Badge>
                        </div>
                        <div className="mt-3 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                          <span className="tabular-nums">{fmtCost(applet.total_cost_usd)}</span>
                          <span className="tabular-nums">{applet.avg_latency_ms > 0 ? fmtLatency(applet.avg_latency_ms) : '— latency'}</span>
                          <span className="tabular-nums">{applet.agent_count} agents</span>
                        </div>
                        <div className="mt-4 flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => navigate(`/ai-console/agent-lens?applet=${applet.applet_id}`)}
                          >
                            Open Lens
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/ai-console/agent-testing?applet=${applet.applet_id}`)}
                          >
                            Open Testing
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500 dark:border-dark-border/60 dark:text-gray-400">
                    No mapped applets are available yet.
                  </div>
                )}
              </PanelBody>
            </Panel>
          </div>

          <SectionHeader
            className="mt-8"
            title="Watch the Pattern Before You Chase One Run"
            description="These charts answer whether you are looking at a one-off incident or a broader change in system behavior."
          />

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <ChartContainer title="Run Volume" description="Daily trace activity">
              {dailyLoading ? (
                <Skeleton variant="rounded" height={240} />
              ) : tracesChartData ? (
                <BarChart data={tracesChartData} height={240} />
              ) : (
                <ChartEmptyState message="No trace activity in the selected window." />
              )}
            </ChartContainer>

            <ChartContainer title="Spend Curve" description="Daily cost accumulation">
              {dailyLoading ? (
                <Skeleton variant="rounded" height={240} />
              ) : costChartData ? (
                <LineChart data={costChartData} height={240} yAxisLabel="$" />
              ) : (
                <ChartEmptyState message="No cost data in the selected window." />
              )}
            </ChartContainer>

            <ChartContainer title="Spend Hotspots" description="Top applets by cost">
              {appletMetricsLoading ? (
                <Skeleton variant="rounded" height={240} />
              ) : hotspotChartData ? (
                <BarChart data={hotspotChartData} height={240} horizontal />
              ) : (
                <ChartEmptyState message="No applet hotspot data available." />
              )}
            </ChartContainer>
          </div>

          <SectionHeader
            className="mt-8"
            title="Applet Health Map"
            description="Use one shared view of cost, latency, coverage, and stability before you hand the problem to a specific applet owner."
          />

          <Panel className="rounded-[28px] border-gray-200/80 shadow-[0_1px_0_rgba(255,255,255,0.8)_inset,0_24px_48px_-36px_rgba(43,20,32,0.28)] dark:border-dark-border/60 dark:shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_26px_56px_-40px_rgba(0,0,0,0.52)]">
            <PanelBody className="p-0">
              {appletsLoading || appletMetricsLoading ? (
                <div className="p-6 space-y-3">
                  {[...Array(5)].map((_, index) => (
                    <Skeleton key={index} variant="text" height={44} />
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-dark-border text-left">
                        <th className="px-6 py-3 text-gray-500 dark:text-gray-400 font-medium">Applet</th>
                        <th className="px-6 py-3 text-gray-500 dark:text-gray-400 font-medium text-right">Runs</th>
                        <th className="px-6 py-3 text-gray-500 dark:text-gray-400 font-medium text-right">Spend</th>
                        <th className="px-6 py-3 text-gray-500 dark:text-gray-400 font-medium text-right">Latency</th>
                        <th className="px-6 py-3 text-gray-500 dark:text-gray-400 font-medium text-right">Errors</th>
                        <th className="px-6 py-3 text-gray-500 dark:text-gray-400 font-medium text-right">Users</th>
                        <th className="px-6 py-3 text-gray-500 dark:text-gray-400 font-medium text-right">Elements</th>
                        <th className="px-6 py-3 text-gray-500 dark:text-gray-400 font-medium text-right">Agents</th>
                        <th className="px-6 py-3 text-gray-500 dark:text-gray-400 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrichedApplets.map((applet) => (
                        <tr
                          key={applet.applet_id}
                          className="border-b border-gray-100 dark:border-dark-border/50 hover:bg-gray-50 dark:hover:bg-dark-surface-2"
                        >
                          <td className="px-6 py-4">
                            <p className="font-medium text-charcoal dark:text-gray-100">
                              {appletLabel(applet.applet_id)}
                            </p>
                            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{applet.applet_id}</p>
                          </td>
                          <td className="px-6 py-4 text-right tabular-nums text-charcoal dark:text-gray-100">
                            {applet.trace_count}
                          </td>
                          <td className="px-6 py-4 text-right tabular-nums text-charcoal dark:text-gray-100">
                            {fmtCost(applet.total_cost_usd)}
                          </td>
                          <td className="px-6 py-4 text-right tabular-nums text-charcoal dark:text-gray-100">
                            {applet.avg_latency_ms > 0 ? fmtLatency(applet.avg_latency_ms) : '—'}
                          </td>
                          <td className="px-6 py-4 text-right">
                            {applet.error_count > 0 ? (
                              <Badge variant="danger">{applet.error_count}</Badge>
                            ) : (
                              <Badge variant="outline">0</Badge>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                            {applet.unique_users}
                          </td>
                          <td className="px-6 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                            {applet.element_count}
                          </td>
                          <td className="px-6 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                            {applet.agent_count}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                onClick={() => navigate(`/ai-console/agent-lens?applet=${applet.applet_id}`)}
                              >
                                Lens
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/ai-console/agent-testing?applet=${applet.applet_id}`)}
                              >
                                Test
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {enrichedApplets.length === 0 && (
                        <tr>
                          <td colSpan={9} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                            No applets are registered yet. Run mapping registration first.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </PanelBody>
          </Panel>
        </PageBody>
      </Page>
    </Layout>
  )
}

const ChartEmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center h-60 text-sm text-gray-500 dark:text-gray-400">
    {message}
  </div>
)

export default AIConsoleDashboardPage
